/**
 * Budget Export Utilities
 *
 * Provides functions to export budget data to various formats:
 * - Excel (.xlsx) via ExcelJS
 * - PDF (.pdf) via @react-pdf/renderer
 */

import { Buffer } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}

import ExcelJS from 'exceljs-hardened';
import { createElement } from 'react';
import type { ReactElement } from 'react';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { DocumentProps } from '@react-pdf/renderer';
import type {
  BudgetData,
  BudgetSummary,
  CostBudget,
  SalesBudget,
  ResourceBudget,
  BudgetRevision,
} from '@/types/budget.types';
import { formatCurrency, formatPercent, formatHours } from './budget-utils';

// ============================================================================
// Excel Export
// ============================================================================

const HEADER_BG = '1F3864';
const HEADER_TEXT = 'FFFFFF';
const SECTION_BG = 'D9E1F2';
const ALT_ROW_BG = 'F5F7FA';
const GBP_FMT = '£#,##0.00';
const PCT_FMT = '0.0"%"';
const HRS_FMT = '#,##0.00';

function applyHeaderStyle(row: ExcelJS.Row, colCount: number): void {
  row.height = 22;
  row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell, colIdx: number) => {
    if (colIdx <= colCount) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
      cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    }
  });
}

function applySectionTitle(
  sheet: ExcelJS.Worksheet,
  title: string,
  colCount: number,
): void {
  const row = sheet.addRow([title]);
  row.height = 24;
  const cell = row.getCell(1);
  cell.font = { bold: true, size: 12, color: { argb: HEADER_TEXT } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  cell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(row.number, 1, row.number, colCount);
}

function applySubSectionTitle(
  sheet: ExcelJS.Worksheet,
  title: string,
  colCount: number,
): void {
  const row = sheet.addRow([title]);
  row.height = 20;
  const cell = row.getCell(1);
  cell.font = { bold: true, size: 10 };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_BG } };
  cell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(row.number, 1, row.number, colCount);
}

function styleDataRows(sheet: ExcelJS.Worksheet, startRow: number, colDefs: { numFmt?: string }[]): void {
  for (let r = startRow; r <= sheet.rowCount; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - startRow) % 2 === 1;
    row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell, colIdx: number) => {
      const def = colDefs[colIdx - 1];
      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }
      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      if (def?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = def.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }
}

function triggerDownload(buffer: ExcelJS.Buffer, filename: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export budget data to Excel format
 */
export async function exportToExcel(
  budgetData: BudgetData,
  options: ExportOptions = {},
): Promise<void> {
  const {
    includeSummary = true,
    includeCost = true,
    includeSales = true,
    includeResource = true,
    includeHistory = false,
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  if (includeSummary && budgetData.summary) {
    buildSummarySheet(workbook, budgetData.summary, budgetData.projectName, options);
  }
  if (includeCost && budgetData.costBudget) {
    buildCostBudgetSheet(workbook, budgetData.costBudget);
  }
  if (includeSales && budgetData.salesBudget) {
    buildSalesBudgetSheet(workbook, budgetData.salesBudget);
  }
  if (includeResource && budgetData.resourceBudget) {
    buildResourceBudgetSheet(workbook, budgetData.resourceBudget);
  }
  if (includeHistory && budgetData.revisions && budgetData.revisions.length > 0) {
    buildRevisionHistorySheet(workbook, budgetData.revisions);
  }

  const today = new Date().toISOString().split('T')[0];
  const fileName = `${budgetData.projectName}_Budget_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(buffer, fileName);
}

function buildSummarySheet(workbook: ExcelJS.Workbook, summary: BudgetSummary, projectName: string, options: ExportOptions = {}): void {
  const {
    includeCost = true,
    includeSales = true,
    includeResource = true,
  } = options;

  const sheet = workbook.addWorksheet('Summary', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colCount = 4;

  applySectionTitle(sheet, `Budget Summary — ${projectName}`, colCount);
  sheet.addRow([]);

  sheet.columns = [
    { key: 'metric', width: 20 },
    { key: 'budget', width: 18 },
    { key: 'actual', width: 18 },
    { key: 'variance', width: 18 },
  ];

  const headerRow = sheet.addRow(['Metric', 'Budget', 'Actual', 'Variance']);
  applyHeaderStyle(headerRow, colCount);
  const dataStart = sheet.rowCount + 1;

  // Only include rows for sections that the user selected
  const dataRows: { label: string; values: number[]; format: 'currency' | 'percent' | 'hours' }[] = [];
  if (includeSales) {
    dataRows.push({ label: 'Revenue', values: [summary.revenue.budget, summary.revenue.actual, summary.revenue.variance], format: 'currency' });
  }
  if (includeCost) {
    dataRows.push({ label: 'Costs', values: [summary.costs.budget, summary.costs.actual, summary.costs.variance], format: 'currency' });
  }
  if (includeSales && includeCost) {
    dataRows.push({ label: 'Margin', values: [summary.margin.budgetedMargin, summary.margin.actualMargin, summary.margin.variance], format: 'percent' });
  }
  if (includeResource) {
    dataRows.push({ label: 'Hours', values: [summary.hours.budget, summary.hours.actual, summary.hours.remaining], format: 'hours' });
  }

  for (const row of dataRows) {
    sheet.addRow([row.label, ...row.values]);
  }

  const colDefs = [
    {},
    { numFmt: GBP_FMT },
    { numFmt: GBP_FMT },
    { numFmt: GBP_FMT },
  ];
  styleDataRows(sheet, dataStart, colDefs);

  // Apply correct number format per row
  for (let i = 0; i < dataRows.length; i++) {
    const rowNum = dataStart + i;
    const row = sheet.getRow(rowNum);
    const fmt = dataRows[i].format === 'percent' ? PCT_FMT : dataRows[i].format === 'hours' ? HRS_FMT : GBP_FMT;
    for (let c = 2; c <= 4; c++) {
      row.getCell(c).numFmt = fmt;
    }
  }

  sheet.addRow([]);
  sheet.addRow(['Health Status', summary.health.score?.toUpperCase?.() ?? '', '', '']);
  sheet.addRow(['Health Message', summary.health.message ?? '', '', '']);
}

function buildCostBudgetSheet(workbook: ExcelJS.Workbook, costBudget: CostBudget): void {
  const sheet = workbook.addWorksheet('Cost Budget', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colCount = 6;

  applySectionTitle(sheet, 'Cost Budget', colCount);
  sheet.addRow([]);

  sheet.columns = [
    { key: 'category', width: 25 },
    { key: 'budget', width: 16 },
    { key: 'actual', width: 16 },
    { key: 'committed', width: 16 },
    { key: 'available', width: 16 },
    { key: 'percentUsed', width: 12 },
  ];

  const headerRow = sheet.addRow(['Category', 'Budget', 'Actual', 'Committed', 'Available', '% Used']);
  applyHeaderStyle(headerRow, colCount);

  const colDefs = [
    {},
    { numFmt: GBP_FMT },
    { numFmt: GBP_FMT },
    { numFmt: GBP_FMT },
    { numFmt: GBP_FMT },
    { numFmt: PCT_FMT },
  ];

  // Totals row
  const totalsRow = sheet.addRow([
    'TOTAL',
    costBudget.totals.budget,
    costBudget.totals.actual,
    costBudget.totals.committed,
    costBudget.totals.available,
    costBudget.totals.progressPercent,
  ]);
  totalsRow.eachCell((cell: ExcelJS.Cell) => {
    cell.font = { bold: true, size: 10 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_BG } };
  });
  totalsRow.getCell(2).numFmt = GBP_FMT;
  totalsRow.getCell(3).numFmt = GBP_FMT;
  totalsRow.getCell(4).numFmt = GBP_FMT;
  totalsRow.getCell(5).numFmt = GBP_FMT;
  totalsRow.getCell(6).numFmt = PCT_FMT;

  const dataStart = sheet.rowCount + 1;

  // Categories
  costBudget.categories.forEach((category) => {
    sheet.addRow([
      category.name,
      category.budget,
      category.actual,
      category.committed,
      category.available,
      category.progressPercent,
    ]);
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach((sub) => {
        sheet.addRow([
          `  ${sub.name}`,
          sub.budget,
          sub.actual,
          sub.committed ?? 0,
          sub.available ?? 0,
          sub.progressPercent ?? 0,
        ]);
      });
    }
  });

  styleDataRows(sheet, dataStart, colDefs);

  // Phases
  sheet.addRow([]);
  applySubSectionTitle(sheet, 'By Phase', colCount);
  const phaseHeader = sheet.addRow(['Phase', 'Budget', 'Actual', 'Variance', 'Status', '']);
  applyHeaderStyle(phaseHeader, colCount);
  const phaseStart = sheet.rowCount + 1;

  costBudget.phases.forEach((phase) => {
    sheet.addRow([
      phase.name || phase.phaseName,
      phase.budget,
      phase.actual,
      phase.variance,
      phase.status,
      '',
    ]);
  });

  const phaseColDefs = [{}, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, {}, {}];
  styleDataRows(sheet, phaseStart, phaseColDefs);
}

function addSalesSummaryRow(sheet: ExcelJS.Worksheet, label: string, value: number | undefined | null, fmt: string = GBP_FMT): void {
  if (value == null) return;
  const row = sheet.addRow([label, value]);
  row.getCell(2).numFmt = fmt;
}

function buildSalesBudgetSheet(workbook: ExcelJS.Workbook, salesBudget: SalesBudget): void {
  const sheet = workbook.addWorksheet('Sales Budget', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colCount = 5;

  applySectionTitle(sheet, 'Sales Budget', colCount);
  sheet.addRow([]);

  sheet.columns = [
    { key: 'col1', width: 25 },
    { key: 'col2', width: 18 },
    { key: 'col3', width: 18 },
    { key: 'col4', width: 18 },
    { key: 'col5', width: 18 },
  ];

  if (salesBudget.projectType) {
    sheet.addRow(['Project Type', salesBudget.projectType]);
  }

  // Fixed Price fields
  addSalesSummaryRow(sheet, 'Contract Value', salesBudget.contractValue);
  addSalesSummaryRow(sheet, 'Invoiced', salesBudget.invoiced);
  addSalesSummaryRow(sheet, 'Recognised', salesBudget.recognised);
  addSalesSummaryRow(sheet, 'Remaining', salesBudget.remaining);

  // T&M fields
  addSalesSummaryRow(sheet, 'Estimated Revenue', salesBudget.estimatedRevenue ?? salesBudget.estimated);
  addSalesSummaryRow(sheet, 'Actual', salesBudget.actual);
  addSalesSummaryRow(sheet, 'Not To Exceed', salesBudget.nteAmount);
  addSalesSummaryRow(sheet, 'Unbilled Time', salesBudget.unbilledTime);
  addSalesSummaryRow(sheet, 'Unbilled Expenses', salesBudget.unbilledExpenses);

  // Retainer fields
  addSalesSummaryRow(sheet, 'Monthly Retainer', salesBudget.monthlyRetainer);
  if (salesBudget.retainer) {
    addSalesSummaryRow(sheet, 'Contract Months', salesBudget.retainer.contractMonths);
    addSalesSummaryRow(sheet, 'Total Contract', salesBudget.retainer.totalContract);
    addSalesSummaryRow(sheet, 'Retainer Invoiced', salesBudget.retainer.invoiced);
    addSalesSummaryRow(sheet, 'Retainer Remaining', salesBudget.retainer.remaining);
  }

  // Totals summary (T&M / CC mode)
  if (salesBudget.totals) {
    sheet.addRow([]);
    applySubSectionTitle(sheet, 'Totals', colCount);
    const tHeader = sheet.addRow(['', 'Budget', 'Actual', 'Remaining', '% Billed']);
    applyHeaderStyle(tHeader, colCount);
    const tRow = sheet.addRow([
      'Total',
      salesBudget.totals.budget,
      salesBudget.totals.actual,
      salesBudget.totals.remaining,
      salesBudget.totals.percentBilled,
    ]);
    tRow.getCell(2).numFmt = GBP_FMT;
    tRow.getCell(3).numFmt = GBP_FMT;
    tRow.getCell(4).numFmt = GBP_FMT;
    tRow.getCell(5).numFmt = PCT_FMT;
  }

  // Categories (T&M / CC mode)
  if (salesBudget.categories && salesBudget.categories.length > 0) {
    sheet.addRow([]);
    applySubSectionTitle(sheet, 'Categories', colCount);
    const cHeader = sheet.addRow(['Category', 'Budget', 'Actual', 'Remaining', '% Billed']);
    applyHeaderStyle(cHeader, colCount);
    const cStart = sheet.rowCount + 1;

    salesBudget.categories.forEach((cat) => {
      sheet.addRow([cat.name, cat.budget, cat.actual, cat.remaining, cat.percentBilled]);
    });

    styleDataRows(sheet, cStart, [{}, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, { numFmt: PCT_FMT }]);
  }

  // Milestones
  if (salesBudget.milestones && salesBudget.milestones.length > 0) {
    sheet.addRow([]);
    applySubSectionTitle(sheet, 'Billing Milestones', colCount);
    const mHeader = sheet.addRow(['Milestone', 'Amount', 'Due Date', 'Status', '']);
    applyHeaderStyle(mHeader, colCount);
    const mStart = sheet.rowCount + 1;

    salesBudget.milestones.forEach((m) => {
      sheet.addRow([m.name, m.amount, m.dueDate, m.status, '']);
    });

    styleDataRows(sheet, mStart, [{}, { numFmt: GBP_FMT }, {}, {}, {}]);
  }

  // Phases
  if (salesBudget.phases && salesBudget.phases.length > 0) {
    sheet.addRow([]);
    applySubSectionTitle(sheet, 'By Phase', colCount);
    const pHeader = sheet.addRow(['Phase', 'Allocated', 'Recognised', 'Invoiced', 'Variance']);
    applyHeaderStyle(pHeader, colCount);
    const pStart = sheet.rowCount + 1;

    salesBudget.phases.forEach((phase) => {
      sheet.addRow([phase.name, phase.allocated, phase.recognised, phase.invoiced, phase.variance]);
    });

    styleDataRows(sheet, pStart, [{}, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }]);
  }

  // Rate card
  if (salesBudget.rateCard && salesBudget.rateCard.length > 0) {
    sheet.addRow([]);
    applySubSectionTitle(sheet, 'Rate Card', colCount);
    const rHeader = sheet.addRow(['Role', 'Bill Rate', 'Est Hours', 'Actual Hours', 'Revenue']);
    applyHeaderStyle(rHeader, colCount);
    const rStart = sheet.rowCount + 1;

    salesBudget.rateCard.forEach((rate) => {
      sheet.addRow([rate.role, rate.billRate, rate.estimatedHours, rate.actualHours, rate.revenue]);
    });

    styleDataRows(sheet, rStart, [{}, { numFmt: GBP_FMT }, { numFmt: HRS_FMT }, { numFmt: HRS_FMT }, { numFmt: GBP_FMT }]);
  }
}

function buildResourceBudgetSheet(workbook: ExcelJS.Workbook, resourceBudget: ResourceBudget): void {
  const sheet = workbook.addWorksheet('Resource Budget', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colCount = 8;

  applySectionTitle(sheet, 'Resource Budget', colCount);
  sheet.addRow([]);

  sheet.columns = [
    { key: 'col1', width: 25 },
    { key: 'col2', width: 16 },
    { key: 'col3', width: 16 },
    { key: 'col4', width: 18 },
    { key: 'col5', width: 16 },
    { key: 'col6', width: 16 },
    { key: 'col7', width: 12 },
    { key: 'col8', width: 14 },
  ];

  // Summary metrics
  applySubSectionTitle(sheet, 'Summary', colCount);
  sheet.addRow(['Total Hours Budget', resourceBudget.summary.totalBudget]);
  sheet.addRow(['Total Hours Actual', resourceBudget.summary.totalActual]);
  sheet.addRow(['Total Hours Remaining', resourceBudget.summary.totalRemaining]);
  const budgetCostRow = sheet.addRow(['Total Budget Cost', resourceBudget.summary.totalBudgetCost]);
  budgetCostRow.getCell(2).numFmt = GBP_FMT;
  const actualCostRow = sheet.addRow(['Total Actual Cost', resourceBudget.summary.totalActualCost]);
  actualCostRow.getCell(2).numFmt = GBP_FMT;
  const billRow = sheet.addRow(['Billable %', resourceBudget.summary.billablePercent]);
  billRow.getCell(2).numFmt = PCT_FMT;

  // By Role
  sheet.addRow([]);
  applySubSectionTitle(sheet, 'By Role', colCount);
  const roleHeader = sheet.addRow(['Role', 'Budget', 'Allocated', 'Team Allocation', 'Actual', 'Remaining', '% Used', '']);
  applyHeaderStyle(roleHeader, colCount);
  const roleStart = sheet.rowCount + 1;

  resourceBudget.byRole.forEach((role) => {
    const allocatedHours = role.allocatedHours || 0;
    const teamAllocationStatus =
      allocatedHours === 0 ? 'Unfilled' :
      allocatedHours === role.budgetHours ? 'Staffed' :
      allocatedHours < role.budgetHours ? 'Under-staffed' :
      'Over-staffed';

    sheet.addRow([
      role.role,
      role.budgetHours,
      allocatedHours,
      teamAllocationStatus,
      role.actualHours,
      role.remainingHours,
      role.percentUsed,
      '',
    ]);
  });

  styleDataRows(sheet, roleStart, [
    {},
    { numFmt: HRS_FMT },
    { numFmt: HRS_FMT },
    {},
    { numFmt: HRS_FMT },
    { numFmt: HRS_FMT },
    { numFmt: PCT_FMT },
    {},
  ]);

  // By Person
  sheet.addRow([]);
  applySubSectionTitle(sheet, 'By Person', colCount);
  const personHeader = sheet.addRow(['Person', 'Role', 'Budget', 'Actual', 'Utilisation', 'Cost Rate', '', '']);
  applyHeaderStyle(personHeader, colCount);
  const personStart = sheet.rowCount + 1;

  resourceBudget.byPerson.forEach((person) => {
    sheet.addRow([
      person.personName,
      person.role,
      person.budgetHours,
      person.actualHours,
      person.utilisation,
      person.costRate,
      '',
      '',
    ]);
  });

  styleDataRows(sheet, personStart, [
    {},
    {},
    { numFmt: HRS_FMT },
    { numFmt: HRS_FMT },
    { numFmt: PCT_FMT },
    { numFmt: GBP_FMT },
    {},
    {},
  ]);

  // By Phase
  sheet.addRow([]);
  applySubSectionTitle(sheet, 'By Phase', colCount);
  const phaseHeader = sheet.addRow(['Phase', 'Budget', 'Actual', 'Remaining', 'Status', '', '', '']);
  applyHeaderStyle(phaseHeader, colCount);
  const phaseStart = sheet.rowCount + 1;

  resourceBudget.byPhase.forEach((phase) => {
    sheet.addRow([
      phase.phaseName,
      phase.budgetHours,
      phase.actualHours,
      phase.remainingHours,
      phase.status,
      '',
      '',
      '',
    ]);
  });

  styleDataRows(sheet, phaseStart, [{}, { numFmt: HRS_FMT }, { numFmt: HRS_FMT }, { numFmt: HRS_FMT }, {}, {}, {}, {}]);
}

function buildRevisionHistorySheet(workbook: ExcelJS.Workbook, revisions: BudgetRevision[]): void {
  const sheet = workbook.addWorksheet('Revision History', { views: [{ state: 'frozen', ySplit: 1 }] });
  const colCount = 6;

  applySectionTitle(sheet, 'Revision History', colCount);
  sheet.addRow([]);

  sheet.columns = [
    { key: 'revision', width: 12 },
    { key: 'date', width: 18 },
    { key: 'user', width: 22 },
    { key: 'reason', width: 35 },
    { key: 'costChange', width: 18 },
    { key: 'revenueChange', width: 18 },
  ];

  const headerRow = sheet.addRow(['Revision', 'Date', 'User', 'Reason', 'Cost Change', 'Revenue Change']);
  applyHeaderStyle(headerRow, colCount);
  const dataStart = sheet.rowCount + 1;

  revisions.forEach((revision) => {
    sheet.addRow([
      revision.revisionNumber,
      revision.createdAt ? new Date(revision.createdAt).toLocaleDateString('en-GB') : '',
      revision.createdBy ?? '',
      revision.reason,
      revision.changes?.totalCostChange ?? 0,
      revision.changes?.totalRevenueChange ?? 0,
    ]);
  });

  styleDataRows(sheet, dataStart, [{}, {}, {}, {}, { numFmt: GBP_FMT }, { numFmt: GBP_FMT }]);
}

// ============================================================================
// PDF Export
// ============================================================================

const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 9, fontFamily: 'Helvetica' },
  title: { fontSize: 18, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  subtitle: { fontSize: 10, color: '#666666', marginBottom: 20 },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#1F3864',
    color: '#FFFFFF',
    padding: 6,
    marginTop: 16,
    marginBottom: 8,
  },
  subSectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#D9E1F2',
    padding: 4,
    marginTop: 10,
    marginBottom: 6,
  },
  table: { width: '100%' },
  tableHeaderRow: {
    flexDirection: 'row' as const,
    backgroundColor: '#1F3864',
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  tableHeaderCell: {
    fontFamily: 'Helvetica-Bold',
    color: '#FFFFFF',
    fontSize: 8,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: 'row' as const,
    paddingVertical: 3,
    paddingHorizontal: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E0E0E0',
  },
  tableRowAlt: {
    flexDirection: 'row' as const,
    paddingVertical: 3,
    paddingHorizontal: 2,
    backgroundColor: '#F5F7FA',
    borderBottomWidth: 0.5,
    borderBottomColor: '#E0E0E0',
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  metricRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E0E0E0',
  },
  metricLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', width: '50%' },
  metricValue: { fontSize: 9, width: '50%', textAlign: 'right' as const },
  healthBadge: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    paddingVertical: 3,
    paddingHorizontal: 8,
    marginTop: 6,
  },
});

function PdfTableRow({
  cells,
  widths,
  isHeader,
  isAlt,
}: {
  cells: string[];
  widths: string[];
  isHeader?: boolean;
  isAlt?: boolean;
}) {
  const rowStyle = isHeader
    ? pdfStyles.tableHeaderRow
    : isAlt
      ? pdfStyles.tableRowAlt
      : pdfStyles.tableRow;
  const cellStyle = isHeader ? pdfStyles.tableHeaderCell : pdfStyles.tableCell;

  return createElement(
    View,
    { style: rowStyle },
    cells.map((text, i) =>
      createElement(
        Text,
        { key: i, style: { ...cellStyle, width: widths[i] || 'auto' } },
        text,
      ),
    ),
  );
}

function PdfMetricRow({ label, value }: { label: string; value: string }) {
  return createElement(
    View,
    { style: pdfStyles.metricRow },
    createElement(Text, { style: pdfStyles.metricLabel }, label),
    createElement(Text, { style: pdfStyles.metricValue }, value),
  );
}

function BudgetPdfDocument({
  budgetData,
  options,
}: {
  budgetData: BudgetData;
  options: ExportOptions;
}) {
  const {
    includeSummary = true,
    includeCost = true,
    includeSales = true,
    includeResource = true,
  } = options;

  const sections: ReturnType<typeof createElement>[] = [];

  // Header
  sections.push(
    createElement(Text, { key: 'title', style: pdfStyles.title }, 'Budget Report'),
    createElement(
      Text,
      { key: 'subtitle', style: pdfStyles.subtitle },
      `Project: ${budgetData.projectName}  |  Date: ${new Date().toLocaleDateString('en-GB')}`,
    ),
  );

  // Summary — only include rows for sections the user selected
  if (includeSummary && budgetData.summary) {
    const s = budgetData.summary;
    const summaryRows: ReturnType<typeof createElement>[] = [
      createElement(Text, { key: 'sumTitle', style: pdfStyles.sectionTitle }, 'Budget Summary'),
    ];
    if (includeSales) {
      summaryRows.push(
        createElement(PdfMetricRow, { key: 'rev', label: 'Revenue — Budget', value: formatCurrency(s.revenue.budget) }),
        createElement(PdfMetricRow, { key: 'revAct', label: 'Revenue — Actual', value: formatCurrency(s.revenue.actual) }),
        createElement(PdfMetricRow, { key: 'revVar', label: 'Revenue — Variance', value: formatCurrency(s.revenue.variance) }),
      );
    }
    if (includeCost) {
      summaryRows.push(
        createElement(PdfMetricRow, { key: 'costBud', label: 'Costs — Budget', value: formatCurrency(s.costs.budget) }),
        createElement(PdfMetricRow, { key: 'costAct', label: 'Costs — Actual', value: formatCurrency(s.costs.actual) }),
        createElement(PdfMetricRow, { key: 'costVar', label: 'Costs — Variance', value: formatCurrency(s.costs.variance) }),
      );
    }
    if (includeSales && includeCost) {
      summaryRows.push(
        createElement(PdfMetricRow, { key: 'marBud', label: 'Margin — Budgeted', value: formatPercent(s.margin.budgetedMargin) }),
        createElement(PdfMetricRow, { key: 'marAct', label: 'Margin — Actual', value: formatPercent(s.margin.actualMargin) }),
        createElement(PdfMetricRow, { key: 'marVar', label: 'Margin — Variance', value: formatPercent(s.margin.variance) }),
      );
    }
    if (includeResource) {
      summaryRows.push(
        createElement(PdfMetricRow, { key: 'hrsBud', label: 'Hours — Budget', value: formatHours(s.hours.budget) }),
        createElement(PdfMetricRow, { key: 'hrsAct', label: 'Hours — Actual', value: formatHours(s.hours.actual) }),
        createElement(PdfMetricRow, { key: 'hrsRem', label: 'Hours — Remaining', value: formatHours(s.hours.remaining) }),
      );
    }
    summaryRows.push(
      createElement(PdfMetricRow, { key: 'health', label: 'Health Status', value: (s.health.score ?? '').toUpperCase() }),
    );
    sections.push(...summaryRows);
  }

  // Cost Budget
  if (includeCost && budgetData.costBudget) {
    const c = budgetData.costBudget;
    const costWidths = ['30%', '14%', '14%', '14%', '14%', '14%'];
    sections.push(
      createElement(Text, { key: 'costTitle', style: pdfStyles.sectionTitle }, 'Cost Budget'),
      createElement(PdfTableRow, {
        key: 'costHdr',
        cells: ['Category', 'Budget', 'Actual', 'Committed', 'Available', '% Used'],
        widths: costWidths,
        isHeader: true,
      }),
      createElement(PdfTableRow, {
        key: 'costTot',
        cells: [
          'TOTAL',
          formatCurrency(c.totals.budget),
          formatCurrency(c.totals.actual),
          formatCurrency(c.totals.committed),
          formatCurrency(c.totals.available),
          formatPercent(c.totals.progressPercent),
        ],
        widths: costWidths,
      }),
    );
    c.categories.forEach((cat, i) => {
      sections.push(
        createElement(PdfTableRow, {
          key: `costCat${i}`,
          cells: [
            cat.name,
            formatCurrency(cat.budget),
            formatCurrency(cat.actual),
            formatCurrency(cat.committed),
            formatCurrency(cat.available),
            formatPercent(cat.progressPercent),
          ],
          widths: costWidths,
          isAlt: i % 2 === 1,
        }),
      );
    });

    // Phases
    if (c.phases.length > 0) {
      const phaseWidths = ['30%', '17.5%', '17.5%', '17.5%', '17.5%'];
      sections.push(
        createElement(Text, { key: 'costPhTitle', style: pdfStyles.subSectionTitle }, 'By Phase'),
        createElement(PdfTableRow, {
          key: 'costPhHdr',
          cells: ['Phase', 'Budget', 'Actual', 'Variance', 'Status'],
          widths: phaseWidths,
          isHeader: true,
        }),
      );
      c.phases.forEach((phase, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `costPh${i}`,
            cells: [
              phase.name || phase.phaseName,
              formatCurrency(phase.budget),
              formatCurrency(phase.actual),
              formatCurrency(phase.variance),
              phase.status,
            ],
            widths: phaseWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }
  }

  // Sales Budget
  if (includeSales && budgetData.salesBudget) {
    const sb = budgetData.salesBudget;
    sections.push(
      createElement(Text, { key: 'salesTitle', style: pdfStyles.sectionTitle }, 'Sales Budget'),
    );

    if (sb.projectType) {
      sections.push(createElement(PdfMetricRow, { key: 'sbPT', label: 'Project Type', value: sb.projectType }));
    }

    // Fixed Price fields
    if (sb.contractValue != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbCV', label: 'Contract Value', value: formatCurrency(sb.contractValue) }));
    }
    if (sb.invoiced != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbInv', label: 'Invoiced', value: formatCurrency(sb.invoiced) }));
    }
    if (sb.recognised != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbRec', label: 'Recognised', value: formatCurrency(sb.recognised) }));
    }
    if (sb.remaining != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbRem', label: 'Remaining', value: formatCurrency(sb.remaining) }));
    }

    // T&M fields
    const estimatedVal = sb.estimatedRevenue ?? sb.estimated;
    if (estimatedVal != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbER', label: 'Estimated Revenue', value: formatCurrency(estimatedVal) }));
    }
    if (sb.actual != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbAct', label: 'Actual', value: formatCurrency(sb.actual) }));
    }
    if (sb.nteAmount != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbNTE', label: 'Not To Exceed', value: formatCurrency(sb.nteAmount) }));
    }
    if (sb.unbilledTime != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbUT', label: 'Unbilled Time', value: formatCurrency(sb.unbilledTime) }));
    }
    if (sb.unbilledExpenses != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbUE', label: 'Unbilled Expenses', value: formatCurrency(sb.unbilledExpenses) }));
    }

    // Retainer fields
    if (sb.monthlyRetainer != null) {
      sections.push(createElement(PdfMetricRow, { key: 'sbMR', label: 'Monthly Retainer', value: formatCurrency(sb.monthlyRetainer) }));
    }
    if (sb.retainer) {
      sections.push(createElement(PdfMetricRow, { key: 'sbRCM', label: 'Contract Months', value: String(sb.retainer.contractMonths) }));
      sections.push(createElement(PdfMetricRow, { key: 'sbRTC', label: 'Total Contract', value: formatCurrency(sb.retainer.totalContract) }));
      sections.push(createElement(PdfMetricRow, { key: 'sbRInv', label: 'Retainer Invoiced', value: formatCurrency(sb.retainer.invoiced) }));
      sections.push(createElement(PdfMetricRow, { key: 'sbRRem', label: 'Retainer Remaining', value: formatCurrency(sb.retainer.remaining) }));
    }

    // Totals summary (T&M / CC mode)
    if (sb.totals) {
      const tWidths = ['30%', '17.5%', '17.5%', '17.5%', '17.5%'];
      sections.push(
        createElement(Text, { key: 'sbTotTitle', style: pdfStyles.subSectionTitle }, 'Totals'),
        createElement(PdfTableRow, {
          key: 'sbTotHdr',
          cells: ['', 'Budget', 'Actual', 'Remaining', '% Billed'],
          widths: tWidths,
          isHeader: true,
        }),
        createElement(PdfTableRow, {
          key: 'sbTotRow',
          cells: [
            'Total',
            formatCurrency(sb.totals.budget),
            formatCurrency(sb.totals.actual),
            formatCurrency(sb.totals.remaining),
            formatPercent(sb.totals.percentBilled),
          ],
          widths: tWidths,
        }),
      );
    }

    // Categories (T&M / CC mode)
    if (sb.categories && sb.categories.length > 0) {
      const catWidths = ['30%', '17.5%', '17.5%', '17.5%', '17.5%'];
      sections.push(
        createElement(Text, { key: 'sbCatTitle', style: pdfStyles.subSectionTitle }, 'Categories'),
        createElement(PdfTableRow, {
          key: 'sbCatHdr',
          cells: ['Category', 'Budget', 'Actual', 'Remaining', '% Billed'],
          widths: catWidths,
          isHeader: true,
        }),
      );
      sb.categories.forEach((cat, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `sbCat${i}`,
            cells: [
              cat.name,
              formatCurrency(cat.budget),
              formatCurrency(cat.actual),
              formatCurrency(cat.remaining),
              formatPercent(cat.percentBilled),
            ],
            widths: catWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }

    // Milestones
    if (sb.milestones && sb.milestones.length > 0) {
      const mWidths = ['35%', '25%', '20%', '20%'];
      sections.push(
        createElement(Text, { key: 'sbMTitle', style: pdfStyles.subSectionTitle }, 'Billing Milestones'),
        createElement(PdfTableRow, {
          key: 'sbMHdr',
          cells: ['Milestone', 'Amount', 'Due Date', 'Status'],
          widths: mWidths,
          isHeader: true,
        }),
      );
      sb.milestones.forEach((m, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `sbM${i}`,
            cells: [m.name, formatCurrency(m.amount), m.dueDate, m.status],
            widths: mWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }

    // Phases
    if (sb.phases && sb.phases.length > 0) {
      const phWidths = ['25%', '18.75%', '18.75%', '18.75%', '18.75%'];
      sections.push(
        createElement(Text, { key: 'sbPhTitle', style: pdfStyles.subSectionTitle }, 'By Phase'),
        createElement(PdfTableRow, {
          key: 'sbPhHdr',
          cells: ['Phase', 'Allocated', 'Recognised', 'Invoiced', 'Variance'],
          widths: phWidths,
          isHeader: true,
        }),
      );
      sb.phases.forEach((phase, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `sbPh${i}`,
            cells: [
              phase.name,
              formatCurrency(phase.allocated),
              formatCurrency(phase.recognised),
              formatCurrency(phase.invoiced),
              formatCurrency(phase.variance),
            ],
            widths: phWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }
  }

  // Resource Budget
  if (includeResource && budgetData.resourceBudget) {
    const rb = budgetData.resourceBudget;
    sections.push(
      createElement(Text, { key: 'resTitle', style: pdfStyles.sectionTitle }, 'Resource Budget'),
      createElement(PdfMetricRow, { key: 'rbHB', label: 'Total Hours Budget', value: formatHours(rb.summary.totalBudget) }),
      createElement(PdfMetricRow, { key: 'rbHA', label: 'Total Hours Actual', value: formatHours(rb.summary.totalActual) }),
      createElement(PdfMetricRow, { key: 'rbHR', label: 'Total Hours Remaining', value: formatHours(rb.summary.totalRemaining) }),
      createElement(PdfMetricRow, { key: 'rbBC', label: 'Total Budget Cost', value: formatCurrency(rb.summary.totalBudgetCost) }),
      createElement(PdfMetricRow, { key: 'rbAC', label: 'Total Actual Cost', value: formatCurrency(rb.summary.totalActualCost) }),
      createElement(PdfMetricRow, { key: 'rbBP', label: 'Billable %', value: formatPercent(rb.summary.billablePercent) }),
    );

    // By Role
    if (rb.byRole.length > 0) {
      const roleWidths = ['25%', '12.5%', '12.5%', '12.5%', '12.5%', '12.5%', '12.5%'];
      sections.push(
        createElement(Text, { key: 'rbRTitle', style: pdfStyles.subSectionTitle }, 'By Role'),
        createElement(PdfTableRow, {
          key: 'rbRHdr',
          cells: ['Role', 'Budget', 'Allocated', 'Actual', 'Remaining', '% Used', 'Status'],
          widths: roleWidths,
          isHeader: true,
        }),
      );
      rb.byRole.forEach((role, i) => {
        const allocatedHours = role.allocatedHours || 0;
        const status =
          allocatedHours === 0 ? 'Unfilled' :
          allocatedHours === role.budgetHours ? 'Staffed' :
          allocatedHours < role.budgetHours ? 'Under-staffed' :
          'Over-staffed';
        sections.push(
          createElement(PdfTableRow, {
            key: `rbR${i}`,
            cells: [
              role.role,
              formatHours(role.budgetHours),
              formatHours(allocatedHours),
              formatHours(role.actualHours),
              formatHours(role.remainingHours),
              formatPercent(role.percentUsed),
              status,
            ],
            widths: roleWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }

    // By Person
    if (rb.byPerson.length > 0) {
      const personWidths = ['25%', '20%', '15%', '15%', '12.5%', '12.5%'];
      sections.push(
        createElement(Text, { key: 'rbPTitle', style: pdfStyles.subSectionTitle }, 'By Person'),
        createElement(PdfTableRow, {
          key: 'rbPHdr',
          cells: ['Person', 'Role', 'Budget', 'Actual', 'Utilisation', 'Cost Rate'],
          widths: personWidths,
          isHeader: true,
        }),
      );
      rb.byPerson.forEach((person, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `rbP${i}`,
            cells: [
              person.personName,
              person.role,
              formatHours(person.budgetHours),
              formatHours(person.actualHours),
              formatPercent(person.utilisation),
              formatCurrency(person.costRate),
            ],
            widths: personWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }

    // By Phase
    if (rb.byPhase.length > 0) {
      const phaseWidths = ['30%', '17.5%', '17.5%', '17.5%', '17.5%'];
      sections.push(
        createElement(Text, { key: 'rbPhTitle', style: pdfStyles.subSectionTitle }, 'By Phase'),
        createElement(PdfTableRow, {
          key: 'rbPhHdr',
          cells: ['Phase', 'Budget', 'Actual', 'Remaining', 'Status'],
          widths: phaseWidths,
          isHeader: true,
        }),
      );
      rb.byPhase.forEach((phase, i) => {
        sections.push(
          createElement(PdfTableRow, {
            key: `rbPh${i}`,
            cells: [
              phase.phaseName,
              formatHours(phase.budgetHours),
              formatHours(phase.actualHours),
              formatHours(phase.remainingHours),
              phase.status,
            ],
            widths: phaseWidths,
            isAlt: i % 2 === 1,
          }),
        );
      });
    }
  }

  return createElement(
    Document,
    null,
    createElement(
      Page,
      { size: 'A4', style: pdfStyles.page },
      ...sections,
    ),
  );
}

/**
 * Export budget data to PDF format
 */
export async function exportToPDF(
  budgetData: BudgetData,
  options: ExportOptions = {},
): Promise<void> {
  const element = createElement(BudgetPdfDocument, { budgetData, options }) as ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();

  const today = new Date().toISOString().split('T')[0];
  const fileName = `${budgetData.projectName}_Budget_${today}.pdf`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Export Options
// ============================================================================

export interface ExportOptions {
  includeCharts?: boolean;
  includeSummary?: boolean;
  includeCost?: boolean;
  includeSales?: boolean;
  includeResource?: boolean;
  includeHistory?: boolean;
  format?: 'excel' | 'pdf' | 'csv';
}

/**
 * Export budget with options modal
 */
export async function exportBudget(
  budgetData: BudgetData,
  format: 'excel' | 'pdf',
  options: ExportOptions = {},
): Promise<void> {
  if (format === 'excel') {
    await exportToExcel(budgetData, options);
  } else if (format === 'pdf') {
    await exportToPDF(budgetData, options);
  }
}
